import { speciesTierLabel } from "../lib/weatherCopy";
import type { AdvisorySpeciesOut } from "../types";

const TIER_DOT_CLASS: Record<string, string> = {
  mild: "heat-species-dot--mild",
  moderate: "heat-species-dot--moderate",
  severe: "heat-species-dot--severe",
};

/** `AdvisorySpeciesOut.tier` -> the color-dot modifier class — shared by
 * every species-breakdown surface (C1's pill/hero detail, C5's Today
 * Card pill detail) so the tier -> color mapping never drifts between
 * them. Fails safe toward the calmest (`mild`) dot for anything
 * unrecognized, matching `weatherCopy.ts`'s own "never throw on an
 * unmapped value" posture. */
export function heatTierDotClass(tier: string): string {
  return TIER_DOT_CLASS[tier] ?? "heat-species-dot--mild";
}

export interface HeatSpeciesListProps {
  species: AdvisorySpeciesOut[];
}

/**
 * Sprint-015 C1/C5 shared species-by-species breakdown list — color-dot +
 * visible name + visible tier text, never color-alone (WCAG 1.4.1).
 * Extracted from `HeatAdvisorySection.tsx` (sprint-015 C1/C2's original,
 * local-only implementation) so C5's Today Card pill detail sheet
 * (`TodayHeatPill.tsx`) can reuse the SAME species-breakdown markup
 * verbatim rather than a second copy-pasted implementation — one
 * component, every detail surface that needs a species list renders it
 * identically.
 */
export function HeatSpeciesList({ species }: HeatSpeciesListProps) {
  return (
    <ul class="heat-species-list">
      {species.map((s) => (
        <li key={s.species_label} class="heat-species-row">
          <span class={`heat-species-dot ${heatTierDotClass(s.tier)}`} aria-hidden="true" />
          <span class="heat-species-name">{s.species_label}</span>
          <span class="heat-species-tier">{speciesTierLabel(s.tier)}</span>
        </li>
      ))}
    </ul>
  );
}
